/**
 * CSV writing for files that leave the application.
 *
 * These downloads are attached to control evidence and read by people who are
 * not going to open them in a text editor to check whether the columns lined
 * up. A value containing a comma silently shifts every field after it by one
 * on that row only — so a single report name like "Cash, Bank and Treasury"
 * moves an actor into the operation column and a timestamp into the actor
 * column, for that row, in a file that otherwise parses perfectly.
 *
 * The eDiscovery export built its rows with `.join(",")` and no quoting at
 * all, over fields that include report paths, object identifiers and free-text
 * keywords.
 *
 * RFC 4180: wrap in double quotes if the value contains a comma, a double
 * quote, a carriage return or a newline, and double any contained quotes.
 */

export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : String(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function csvRow(values: readonly unknown[]): string {
  return values.map(csvCell).join(",");
}

/**
 * A CSV document from a header row and body rows.
 *
 * CRLF line endings, because that is what RFC 4180 specifies and what Excel
 * expects; a lone LF makes some versions treat the whole file as one row.
 *
 * The BOM is deliberate. Without it Excel on Windows decodes the file as the
 * system code page, so a non-ASCII display name in an audit export arrives
 * mangled — and a mangled actor name in evidence is worse than an awkward
 * first byte.
 */
export function csvDocument(
  header: readonly string[],
  rows: readonly (readonly unknown[])[]
): string {
  return "\uFEFF" + [csvRow(header), ...rows.map(csvRow)].join("\r\n");
}

export function downloadCsv(filename: string, contents: string): void {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
